import { useState } from 'react';
import { Container, Title, Table, Text, Badge, Paper, Group, TextInput, SimpleGrid } from '@mantine/core';
import { useSeasonalCalendar, usePreOrderWindows } from '@/hooks/usePortalApi';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthColor(month: number, peak: number[] | null, available: number[] | null): string {
  if (peak?.includes(month)) return 'green';
  if (available?.includes(month)) return 'yellow';
  return 'gray';
}

export default function SeasonalCalendar() {
  const [commodity, setCommodity] = useState('');
  const [region, setRegion] = useState('');
  const { data: calData } = useSeasonalCalendar(commodity || undefined, region || undefined);
  const { data: windowData } = usePreOrderWindows(commodity || undefined);

  const calendar = calData?.calendar ?? [];
  const windows = windowData?.windows ?? [];

  return (
    <Container size="lg" py="md">
      <Title order={2} mb="md">Seasonal Calendar</Title>

      <Group mb="md">
        <TextInput placeholder="Filter by commodity" value={commodity} onChange={(e) => setCommodity(e.currentTarget.value)} />
        <TextInput placeholder="Filter by region" value={region} onChange={(e) => setRegion(e.currentTarget.value)} />
      </Group>

      <Table striped mb="xl">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Commodity</Table.Th>
            <Table.Th>Region</Table.Th>
            {MONTHS.map((m) => <Table.Th key={m} style={{ textAlign: 'center', fontSize: 11 }}>{m}</Table.Th>)}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {calendar.map((e, idx) => (
            <Table.Tr key={idx}>
              <Table.Td fw={600}>{e.commodity}</Table.Td>
              <Table.Td>{e.region}</Table.Td>
              {MONTHS.map((_, i) => (
                <Table.Td key={i} style={{ textAlign: 'center' }}>
                  <Badge size="xs" color={monthColor(i + 1, e.peak_months, e.months_available)} variant="filled" circle>
                    &nbsp;
                  </Badge>
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
          {calendar.length === 0 && (
            <Table.Tr><Table.Td colSpan={14}><Text c="dimmed" ta="center">No seasonality data found</Text></Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Group mb="xs" gap={8}>
        <Badge color="green" size="xs">Peak</Badge>
        <Badge color="yellow" size="xs">Available</Badge>
        <Badge color="gray" size="xs">Unavailable</Badge>
      </Group>

      {windows.length > 0 && (
        <>
          <Title order={3} mt="xl" mb="sm">Pre-Order Windows</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {windows.map((w, idx) => (
              <Paper key={idx} p="md" withBorder>
                <Text fw={600}>{w.commodity}</Text>
                <Text size="sm" c="dimmed">{w.source_region}</Text>
                <Group mt="xs" gap="xs">
                  <Badge>{w.window_start} - {w.window_end}</Badge>
                  {w.expected_fob_price && <Badge variant="light">${w.expected_fob_price.toFixed(2)} FOB</Badge>}
                  {w.lead_time_days && <Badge variant="light">{w.lead_time_days}d lead</Badge>}
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </>
      )}
    </Container>
  );
}
